import { useMemo } from 'react'
import { captureException } from '@sentry/react'
import { searchStateTools } from '@sellpy/commons'
import useSWR, { mutate, preload } from 'swr'
import { getAlgoliaLocaleApplication } from '../lib/algolia'
import { getRegion } from '../../common/region/region'

const EMPTY_ARRAY = []

/** @satisfies {import('swr').SWRConfiguration} */
const SWR_OPTIONS = {
  revalidateOnFocus: false,
  dedupingInterval: 5 * 60 * 1000, // 5 minutes
  errorRetryCount: 1,
  revalidateOnReconnect: false,
  onError: (error) => captureException(error)
}

const { searchSingleIndex, marketItemIndexes } = getAlgoliaLocaleApplication()

/**
 * @typedef {["algolia-item", string]} AlgoliaSingleItemFetcherKey
 * @type {import('swr').Fetcher<AlgoliaItem | null, AlgoliaSingleItemFetcherKey>}
 */
const algoliaSingleItemFetcher = async ([, itemId]) => {
  try {
    // For single items we don't need the default filters, since if you already have the ID of the item you want to fetch somehow.
    // We use search instead of getObject as we have algoliasearch/lite in the client bundle, which doesn't have getObject.

    const res = await searchSingleIndex({
      indexName: marketItemIndexes.salesStartDescending,
      searchParams: {
        query: '',
        filters: `objectID:${itemId}`,
        hitsPerPage: 1
      }
    })
    return res.hits[0] || null
  } catch (error) {
    if (error.status === 404) return null
    throw error
  }
}

/**
 * @typedef {["algolia-items", string[], {excludeReserved?: boolean, withRelevanceIndex?: boolean}]} AlgoliaMultiItemFetcherKey
 * @type {import('swr').Fetcher<AlgoliaItem[], AlgoliaMultiItemFetcherKey>}
 */
const algoliaMultiItemFetcher = async ([
  ,
  itemIds,
  { excludeReserved = false, excludeP2p = false, withRelevanceIndex = false }
]) => {
  if (!itemIds.length) return EMPTY_ARRAY
  const indexName = withRelevanceIndex
    ? marketItemIndexes.relevance
    : marketItemIndexes.salesStartDescending
  const { hits } = await searchSingleIndex({
    indexName,
    searchParams: {
      query: '',
      filters: searchStateTools.composeDefaultAlgoliaFilters({
        excludeReserved,
        excludeP2p,
        region: getRegion()
      }),
      facetFilters: [[...new Set(itemIds)].map((itemId) => `objectID:${itemId}`)],
      hitsPerPage: 1000
    }
  })
  return hits || EMPTY_ARRAY
}

/**
 * @param {string} itemId
 */
export const getAlgoliaItem = async (itemId) =>
  preload(['algolia-item', itemId], algoliaSingleItemFetcher)

/**
 * Caches an Algolia item, probably recieved from getAlgoliaItems, for usage with the useAlgoliaItem hook.
 * @param {AlgoliaItem} item
 */
export const cacheAlgoliaItem = async (item) => mutate(['algolia-item', item.objectID], item)

/**
 * @param {object} props
 * @param {string | null} props.itemId (pass null to skip fetching)
 * @param {boolean} [props.suspense=false]
 */
export const useAlgoliaItem = ({ itemId, suspense = false }) => {
  const { data: item, isLoading } = useSWR(
    itemId ? ['algolia-item', itemId] : null,
    algoliaSingleItemFetcher,
    {
      ...SWR_OPTIONS,
      suspense
    }
  )
  return { item, isLoading }
}

/**
 * @param {string[]} itemIds
 */
export const useAlgoliaItems = (
  itemIds,
  {
    excludeReserved = false,
    excludeP2p = false,
    withRelevanceIndex = false,
    preserveInputOrder = false,
    suspense = false
  } = {}
) => {
  const { data, isLoading } = useSWR(
    itemIds.length
      ? ['algolia-items', [...itemIds].sort(), { withRelevanceIndex, excludeReserved, excludeP2p }]
      : null,
    algoliaMultiItemFetcher,
    { ...SWR_OPTIONS, suspense }
  )

  const items = useMemo(() => {
    if (data && preserveInputOrder)
      return [...data].sort((a, b) => itemIds.indexOf(a.objectID) - itemIds.indexOf(b.objectID))
    return data
  }, [data, itemIds, preserveInputOrder])

  return { items, isLoading }
}

/**
 * An incomplete definition of an Algolia item object. Fix details if needed.
 * The purpose is autocompletion when working with Algolia items.
 *
 * @typedef {Object} AlgoliaItem
 * @property {string} objectID
 * @property {number} createdAt
 * @property {number} updatedAt
 * @property {string} user
 * @property {string} bag
 * @property {number} weight
 * @property {string} itemPackaging
 * @property {any} metadata
 * @property {string[]} images
 * @property {string[]} sizes
 * @property {boolean} p2p
 * @property {boolean} isReserved
 * @property {string} segment
 * @property {string} itemIO
 * @property {Object} categories
 * @property {string[]} categories.lvl0
 * @property {string[]} categories.lvl1
 * @property {string[]} categories.lvl2
 * @property {string[]} categories.lvl3
 * @property {string} storageSite
 * @property {string[]} searchableExternalCategory
 * @property {string} saleType
 * @property {string[]} titleOutputOrder
 * @property {string[]} bodyOutputOrder
 * @property {number} itemAbTestFraction
 * @property {string[]} keywords
 * @property {string[]} style
 * @property {string[]} brandGroups
 * @property {number} favouriteCount
 * @property {string} salesChannel
 * @property {boolean} lastChance
 * @property {Object} pricing
 * @property {number} pricing.amount
 * @property {string} pricing.currency
 * @property {boolean} isForSale
 * @property {number} saleStartedAt
 * @property {string} itemPriceBucket
 */
